import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { SidebarMenuButton } from 'components/ui/sidebar';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { cn } from '../../../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

// --- Types ---
export interface SidebarRichItemProps {
    title: string;
    subtitle?: string;
    icon?: IconName;
    image?: string;
    isActive?: boolean;
    onClick?: () => void;
    rightAction?: React.ReactNode;
    className?: string;
}

export interface SidebarTreeItemProps {
    label: string;
    icon?: IconName;
    image?: string;
    isActive?: boolean;
    onClick?: () => void;
    children?: React.ReactNode; // For nested items
    rightAction?: React.ReactNode;
    defaultOpen?: boolean;
    depth?: number;
    className?: string;
}


// --- Components ---

/**
 * Rich Item for "My Assistants" or similar.
 * Displays an Icon/Image, a Title, and a Subtitle in a column.
 */
export function SidebarRichItem({
    title,
    subtitle,
    icon,
    image,
    isActive,
    onClick,
    rightAction,
    className,
}: SidebarRichItemProps) {
    const [isHovered, setIsHovered] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!textRef.current) return;
        const checkOverflow = () => {
            if (textRef.current) {
                setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(textRef.current);
        checkOverflow();

        return () => observer.disconnect();
    }, [title]);

    return (
        <SidebarMenuButton
            onClick={onClick}
            isActive={isActive}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn("h-auto py-2 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:py-2 relative group/rich-item", className)}
            style={{ paddingLeft: '40px' }}
        >
            <div className="flex items-center gap-3 w-full group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 pr-9">
                {/* Helper to render Icon or Image */}
                <div className="shrink-0 flex items-center justify-center">
                    {image ? (
                        <img src={image} alt={title} className="h-5 w-5 object-contain rounded-sm" />
                    ) : icon ? (
                        <Icon name={icon} size={20} className={isActive ? "text-primary" : "text-muted-foreground"} />
                    ) : null}
                </div>

                <div className="flex flex-col min-w-0 text-left group-data-[collapsible=icon]:hidden overflow-hidden">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span ref={textRef} className="truncate text-[14px] font-normal leading-[22px] mb-1">{title}</span>
                        </TooltipTrigger>
                        {isOverflowing && (
                            <TooltipContent side="right">
                                {title}
                            </TooltipContent>
                        )}
                    </Tooltip>
                    {subtitle && (
                        <span className="truncate text-xs text-muted-foreground leading-none">
                            {subtitle}
                        </span>
                    )}
                </div>
            </div>

            {rightAction && (
                <div className={cn(
                    "absolute right-2 top-1/2 -translate-y-1/2 transition-opacity group-data-[collapsible=icon]:hidden",
                    isHovered || isActive ? "opacity-100" : "opacity-0"
                )}>
                    {rightAction}
                </div>
            )}
        </SidebarMenuButton>
    );
}

/**
 * Tree Item for "Projects" or "Chats".
 * Supports nesting via children.
 */
export function SidebarTreeItem({
    label,
    icon,
    image,
    isActive,
    onClick,
    children,
    rightAction,
    defaultOpen = false,
    depth = 0,
    className,
}: SidebarTreeItemProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [isHovered, setIsHovered] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!textRef.current) return;
        const checkOverflow = () => {
            if (textRef.current) {
                setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(textRef.current);
        checkOverflow();

        return () => observer.disconnect();
    }, [label]);

    const hasChildren = React.Children.count(children) > 0;

    // Calculation to align text with section title:
    // Section Header has px-3 (12px), Icon (16px), Gap-3 (12px). Text starts at 12 + 16 + 12 = 40px.
    // If Tree Item has no icon, it needs 40px padding-left to align label.
    // If it HAS an icon, it needs 12px (px-3) and then 16+12 for the label.
    const basePadding = 40;
    const depthIndent = 0;
    const paddingLeft = basePadding + (depth * depthIndent);

    const renderRightAction = () => {
        if (!rightAction) return null;
        return (
            <div className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 transition-opacity group-data-[collapsible=icon]:hidden",
                isHovered || isActive ? "opacity-100" : "opacity-0"
            )}>
                {rightAction}
            </div>
        );
    };

    const content = (
        <div className="flex items-center gap-3 min-w-0 w-full group-data-[collapsible=icon]:justify-center pr-9">
            {image ? (
                <img src={image} alt={label} className="h-4 w-4 object-contain rounded-sm shrink-0" />
            ) : icon ? (
                <Icon name={icon} size={16} className="shrink-0" />
            ) : null}
            <div className="flex items-center gap-1 min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span ref={textRef} className="truncate text-[14px] font-normal leading-[22px]">{label}</span>
                    </TooltipTrigger>
                    {isOverflowing && (
                        <TooltipContent className="text-[14px] leading-[22px] font-normal" side="bottom">
                            {label}
                        </TooltipContent>
                    )}
                </Tooltip>
                {hasChildren && (
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 transition-transform text-muted-foreground group-data-[collapsible=icon]:hidden",
                            isOpen && "rotate-180"
                        )}
                    />
                )}
            </div>
        </div>
    );

    // Render basic item if no children
    if (!hasChildren) {
        return (
            <SidebarMenuButton
                onClick={onClick}
                isActive={isActive}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn("h-8 relative group/tree-item", className)}
                style={{ paddingLeft: `${paddingLeft}px` }}
            >
                {content}
                {renderRightAction()}
            </SidebarMenuButton>
        );
    }

    // Render collapsible item if has children
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
            <CollapsibleTrigger asChild>
                <SidebarMenuButton
                    isActive={isActive}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={cn("h-8 group/tree-item relative", className)}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onClick) onClick();
                    }}
                >
                    {content}
                    {renderRightAction()}
                </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-1">
                {children}
            </CollapsibleContent>
        </Collapsible>
    );
}
